/* Approved 11 October 2026: selling amounts round down to whole pounds.
 * Supplier reference data and existing Ethnicraft prices remain unchanged. */
window.FultonsSellingPrice = window.FultonsSellingPrice || ((value, preserveEthnicraft = false) => {
  if (preserveEthnicraft || typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return value;
  return Math.floor(value + 1e-9);
});
/* Harrison Spinks / Somnus: source-listed retail prices, without £5 rounding. */
(() => {
  'use strict';
  const currency = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 });
  const money = value => currency.format(window.FultonsSellingPrice(Number(value))).replace(/\.00$/, '');
  const label = (key, fallback) => window.FultonsHarrisonLabels?.[key] || fallback;
  const make = (tag, value, className) => {
    const node = document.createElement(tag);
    if (value) node.textContent = value;
    if (className) node.className = className;
    return node;
  };
  // Only source-verified, usable pictures may form customer headboard choices.
  const failedHeadboardImages = new Set();
  const visibleHeadboards = group => (group.headboards || []).filter(h =>
    typeof h.image === 'string' && h.image.trim() && h.imageVerified !== false &&
    !h.imageHeld && !failedHeadboardImages.has(h.image));
  let current = null;
  let scheduled = false;
  let promotionTimer = null;
  function clearPromotionTimer() {
    if (promotionTimer !== null) clearTimeout(promotionTimer);
    promotionTimer = null;
  }
  // Revisit UK campaign boundaries even when no selector changes occur.
  const promotionsFor = group => group.promotions || (group.promotion ? [group.promotion] : []);
  const promotionStart = promotion => Date.parse(promotion.startsAt || (promotion.start || '') + 'T00:00:00+01:00');
  const promotionEnd = promotion => Date.parse(promotion.endsAt || (promotion.endExclusive || '') + 'T00:00:00+01:00');
  function armPromotionTimer(products, now = Date.now()) {
    clearPromotionTimer();
    let next = Infinity;
    Object.values(products).forEach(group => {
      if (!['bedset', 'mattress'].includes(group.kind)) return;
      [group.upcomingPromotion?.announceAt, group.upcomingPromotion?.startsAt].forEach(value => {
        const boundary = Date.parse(value || '');
        if (boundary > now) next = Math.min(next, boundary);
      });
      promotionsFor(group).forEach(promotion => {
        const start = promotionStart(promotion);
        const end = promotionEnd(promotion);
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return;
        if (start > now) next = Math.min(next, start);
        if (end > now) next = Math.min(next, end);
      });
    });
    if (Number.isFinite(next)) {
      promotionTimer = setTimeout(() => {
        promotionTimer = null;
        schedule();
      }, Math.min(next - now, 2147483647));
    }
  }
  function cleanup() {
    if (!current) return;
    const { section, headline, createdHeadline, previousPrice, hiddenPrices, offerTag } = current;
    offerTag?.remove();
    const disclosure = section.closest('.fultons-product-disclosure');
    if (disclosure && disclosure.querySelectorAll('section').length === 1) disclosure.remove();
    else section.remove();
    if (createdHeadline) headline.remove();
    else if (headline.isConnected) {
      headline.replaceChildren(...previousPrice);
      headline.removeAttribute('data-harrison-price');
      headline.removeAttribute('data-fixed-piece-price');
    }
    hiddenPrices.forEach(([node, hidden]) => { node.hidden = hidden; });
    document.querySelectorAll('[data-harrison-enquiry]').forEach(node => node.remove());
    current.bedDialog?.remove();
    current.root.querySelector('.fh-bed-visual')?.remove();
    const oldGallery=current.root.querySelector('fultons-product-gallery');if(oldGallery)oldGallery.hidden=false;
    current = null;
  }
  // Explicit campaign offsets preserve London midnight across the autumn clock change.
  function calculateUnrounded(group, option, headboard, now = Date.now()) {
    if (!option || !Number.isFinite(option.priceGbp) || option.priceGbp <= 0) return null;
    if (group.kind === 'mattress') {
      const result = { total: option.priceGbp, regular: option.priceGbp, offer: false, option };
      // Zip/link terms vary between campaigns; never infer eligibility from width alone.
      if (/zip|link/i.test(option.label || '')) return result;
      const promotion = promotionsFor(group).find(item => now >= promotionStart(item) && now < promotionEnd(item));
      const width = Number(option.widthCm);
      const priceWidth = Number(promotion?.sizeUpgrades?.[String(width)] || (width === Number(promotion?.targetWidth) ? promotion.priceWidth : 0));
      const lower = group.options?.find(item => Number(item.widthCm) === priceWidth && !/zip|link/i.test(item.label || ''));
      return lower?.priceGbp > 0 && lower.priceGbp < result.regular
        ? { ...result, total: lower.priceGbp, offer: true, offerLabel: promotion.label, promotion } : result;
    }
    if (group.kind !== 'bedset') return { total: option.priceGbp, regular: option.priceGbp, offer: false, option };
    const width = Number(option.widthCm);
    const base = group.baseOptions?.find(item => Number(item.widthCm) === width);
    const hbPrice = Number(headboard?.prices?.[String(width)]);
    if (!base || !Number.isFinite(base.priceGbp) || base.priceGbp <= 0 || !(hbPrice > 0)) return null;
    const regular = option.priceGbp + base.priceGbp + hbPrice;
    const result = { total: regular, regular, offer: false, option, base, headboard };
    const promotion = promotionsFor(group).find(item => now >= promotionStart(item) && now < promotionEnd(item));
    const priceWidth = Number(promotion?.sizeUpgrades?.[String(width)] || (width === Number(promotion?.targetWidth) ? promotion.priceWidth : 0));
    if (!promotion || !priceWidth) return result;
    const lowerMattress = group.options?.find(item => Number(item.widthCm) === priceWidth);
    const lowerBase = group.baseOptions?.find(item => Number(item.widthCm) === priceWidth);
    const lowerHeadboard = promotion.includeHeadboard ? Number(headboard.prices?.[String(priceWidth)]) : hbPrice;
    if (!(lowerMattress?.priceGbp > 0) || !(lowerBase?.priceGbp > 0) || !(lowerHeadboard > 0)) return result;
    const total = lowerMattress.priceGbp + lowerBase.priceGbp + lowerHeadboard;
    return total < regular ? { ...result, total, offer: true, offerLabel: promotion.label, promotion } : result;
  }
  function calculate(group, option, headboard, now = Date.now()) {
    const result = calculateUnrounded(group, option, headboard, now);
    return result ? {...result, total:window.FultonsSellingPrice(result.total)} : result;
  }
  window.FultonsHarrisonCalculate = calculate;
  function selectedResult(state) {
    const option = state.options[Number(state.select.value)];
    const headboard = state.group.headboards?.find(item => String(item.id) === state.headboardSelect?.value);
    return calculate(state.group, option, headboard);
  }
  function commit(state) {
    const result = selectedResult(state);
    if (!result) return;
    const choice = [state.group.title, result.option.label,
      result.base ? label('base', 'Base') + ': ' + result.base.label : '',
      result.headboard ? label('headboard', 'Headboard') + ': ' + result.headboard.label : '',
      state.fabric ? label('fabric', 'Fabric') + ': ' + state.fabric.name : '',
      state.group.configurator ? state.bedChoice() : state.group.kind === 'bedset' ? (state.group.fixedBaseNote || label('shallowBase', 'Shallow divan base')) : '',
      money(result.total), result.offer ? result.offerLabel : '', state.group.unitNote].filter(Boolean).join(' — ');
    const form = document.querySelector('#FultonsEnquiry form');
    if (form) {
      let field = form.querySelector('input[name="contact[Selected configuration]"]');
      if (!field) { field = make('input'); field.type = 'hidden'; field.name = 'contact[Selected configuration]'; form.append(field); }
      field.dataset.harrisonEnquiry = 'field';
      field.value = choice;
      let summary = form.querySelector('[data-harrison-enquiry="summary"]');
      if (!summary) { summary = make('div', '', 'fultons-harrison-enquiry'); summary.dataset.harrisonEnquiry = 'summary'; form.prepend(summary); }
      if (summary.dataset.selection !== choice) {
        summary.replaceChildren(make('strong', label('selectedOption', 'Your selected option')), make('p', choice));
        summary.dataset.selection = choice;
      }
    }
    state.root.querySelectorAll('a.catalogue-proof__enquire,a.pdp-refined__enquire').forEach(link => {
      const url = new URL(link.href, location.href);
      if (url.searchParams.has('product')) { url.searchParams.set('product', choice); if (link.href !== url.href) link.href = url.href; }
    });
  }
  function update(state) {
    if (state.headboardSelect) {
      const option = state.options[Number(state.select.value)];
      [...state.headboardSelect.options].forEach(item => {
        const headboard = state.group.headboards.find(candidate => String(candidate.id) === item.value);
        const unavailable = !calculate(state.group, option, headboard);
        if (item.disabled !== unavailable) item.disabled = unavailable;
      });
      if (state.headboardSelect.selectedOptions[0]?.disabled) {
        const first = [...state.headboardSelect.options].find(item => !item.disabled);
        if (first) state.headboardSelect.value = first.value;
      }
    }
    const result = selectedResult(state);
    if (!result) return;
    state.section.dataset.selectedSize = state.select.value;
    state.section.dataset.selectedPrice = String(result.total);
    const key = [state.id, state.select.value, state.headboardSelect?.value, result.total, result.regular].join(':');
    if (state.headline.dataset.harrisonPrice !== key) {
      const selected = make('span', '', 'fultons-selected-price');
      const row = make('span', '', 'fap-price-row');
      row.append(make('strong', money(result.total), 'fultons-selected-price__sale'));
      if (result.offer) row.append(make('s', money(result.regular), 'fultons-selected-price__rrp'));
      row.append(state.offerTag);
      selected.append(row);
      state.headline.replaceChildren(selected);
      state.headline.dataset.harrisonPrice = key;
    }
    if (state.offerTag) {
      const text = result.offer ? label(result.promotion?.menuTagKey, result.offerLabel) : '';
      if (state.offerTag.hidden !== !text) state.offerTag.hidden = !text;
      if (state.offerTag.textContent !== text) state.offerTag.textContent = text;
      const detail = result.offer ? result.offerLabel : '';
      if (state.offerTag.title !== detail) state.offerTag.title = detail;
    }
    if (state.offerNote) {
      state.offerNote.hidden = !result.offer;
      state.offerNote.textContent = result.offer ? result.offerLabel : '';
    }
    if (state.upcomingNote) {
      const notice = state.group.upcomingPromotion;
      const now = Date.now();
      const visible = notice && now >= Date.parse(notice.announceAt) && now < Date.parse(notice.startsAt);
      state.upcomingNote.hidden = !visible;
      state.upcomingNote.textContent = visible ? notice.label : '';
    }
    if (state.syncHeadboards) state.syncHeadboards();
    if (state.headboardPreview && !state.syncHeadboards) {
      const headboard = result.headboard;
      const signature = [headboard?.id, headboard?.image, headboard?.showroomNote].join(':');
      if (state.headboardPreview.dataset.selection !== signature) {
        state.headboardPreview.replaceChildren();
        if (headboard?.image) {
          const image = make('img'); image.src = headboard.image; image.alt = headboard.label;
          image.width = 96; image.height = 64; image.loading = 'lazy'; image.decoding = 'async';
          state.headboardPreview.append(image);
        }
        if (headboard?.showroomNote) state.headboardPreview.append(make('p', headboard.showroomNote, 'fultons-harrison-note'));
        state.headboardPreview.hidden = !state.headboardPreview.childNodes.length;
        state.headboardPreview.dataset.selection = signature;
      }
    }
    if (state.group.deliveryNote) state.root.querySelectorAll('.fo-estimate').forEach(panel => {
      panel.dataset.pending = state.group.deliveryNote;
      const lead = panel.querySelector('[data-fo-lead]');
      if (lead && lead.textContent !== state.group.deliveryNote) lead.textContent = state.group.deliveryNote;
    });
    state.syncBedImage?.();
    state.syncPurchase?.();
    commit(state);
  }
  function updateMenuOffers(products, now = Date.now()) {
    document.querySelectorAll('.living-test__card[data-product-id],.fo-card[data-product-id]').forEach(card => {
      const group = products[card.dataset.productId];
      const promotion = group && ['bedset', 'mattress'].includes(group.kind) ? promotionsFor(group).find(item => now >= promotionStart(item) && now < promotionEnd(item)) : null;
      const text = promotion?.menuTagKey ? label(promotion.menuTagKey, '') : '';
      const photo = card.querySelector('.living-test__image,.fo-product-photo');
      let badge = card.querySelector('[data-harrison-menu-offer]');
      if (!text || !photo) { badge?.remove(); return; }
      if (!badge) {
        badge = make('span', '', 'fultons-harrison-menu-offer');
        badge.dataset.harrisonMenuOffer = card.dataset.productId;
        photo.append(badge);
      }
      const detail = label(promotion.menuDetailKey, promotion.label || text);
      if (badge.textContent !== text) badge.textContent = text;
      if (badge.title !== detail) badge.title = detail;
      if (badge.getAttribute('aria-label') !== detail) badge.setAttribute('aria-label', detail);
    });
  }
  function updateMenu(products) {
    const guides = window.FultonsMenuGuidePrices;
    if (!guides) return false;
    let changed = false;
    Object.entries(products).forEach(([id, group]) => {
      const amounts = group.kind === 'bedset'
        ? (group.bases ? Object.values(group.bases).flatMap(base => (group.options || []).flatMap(option => visibleHeadboards(group).map(headboard => calculate({...group,baseOptions:base.options},option,headboard)?.total))) : (group.options || []).flatMap(option => visibleHeadboards(group).map(headboard => calculate(group, option, headboard)?.total)))
        : (group.options || []).map(option => calculate(group, option)?.total);
      const prices = amounts.filter(price => Number.isFinite(price) && price > 0);
      if (!prices.length) return;
      const price = Math.min(...prices);
      // The common guide-price owner retains numeric prices for filters/sorting;
      // a scoped prefix avoids fighting its mutation observer over price text.
      document.querySelectorAll('.living-test__card[data-product-id],.fo-card[data-product-id]').forEach(card => {
        if (card.dataset.productId === id) {
          card.dataset.harrisonFrom = 'true';
          card.style.setProperty('--harrison-from-label', JSON.stringify(label('from', 'From') + ' '));
        }
      });
      if (guides[id]?.price === price && guides[id]?.detail === group.title && guides[id]?.basis === (group.unitNote || '')) return;
      guides[id] = { price, from: true, label: money(price), status: 'verified', basis: group.unitNote || '', detail: group.title };
      changed = true;
    });
    return changed;
  }
  function headboardPicker(state) {
    if (!state.headboardSelect) return;
    state.headboardSelect.hidden = true;
    state.headboardSelect.setAttribute('aria-hidden', 'true');
    state.headboardSelect.tabIndex = -1;
    const details = make('details', '', 'fh-fabrics fh-headboards');
    const summary = make('summary');
    const thumb = make('img'); thumb.alt = ''; thumb.width = 44; thumb.height = 44;
    const title = make('span');
    summary.append(thumb, title); details.append(summary);
    const grid = make('div', '', 'fh-fabrics__grid');
    const more = make('button', label('moreHeadboards', 'Show more headboards'), 'fh-fabrics__more'); more.type = 'button';
    const note = make('p', '', 'fultons-harrison-note');
    let limit = 12, signature = '';
    function draw() {
      grid.replaceChildren();
      visibleHeadboards(state.group).slice(0, limit).forEach(headboard => {
        const button = make('button', '', 'fh-fabrics__sample'); button.type = 'button';
        button.disabled = !calculate(state.group, state.options[Number(state.select.value)], headboard);
        button.setAttribute('aria-label', label('selectHeadboard', 'Select headboard') + ': ' + headboard.label);
        button.setAttribute('aria-pressed', String(state.headboardSelect.value === String(headboard.id)));
        const image = make('img'); image.alt = ''; image.width = 160; image.height = 120;
        image.loading = 'lazy'; image.decoding = 'async';
        image.addEventListener('error', () => {
          failedHeadboardImages.add(headboard.image);
          button.remove();
          // Remove the unusable choice without substituting a different selected purchase.
          const failedOption = [...state.headboardSelect.options].find(o => o.value === String(headboard.id));
          if (failedOption?.selected) {
            // Retain its explicit selection and price for enquiry, but never show a blank tile.
            failedOption.disabled = true;
            thumb.hidden = true;
          } else failedOption?.remove();
          signature = ''; draw();
          updateMenu(window.FultonsHarrison?.products || {});
        }, { once: true });
        image.src = headboard.image; button.append(image);
        button.append(make('span', headboard.label));
        if (button.disabled) button.append(make('span', label('unavailableSize', 'Not available in this size'), 'fh-headboards__unavailable'));
        button.addEventListener('click', () => {
          state.headboardSelect.value = String(headboard.id); details.open = false;
          update(state); document.dispatchEvent(new CustomEvent('fultons:catalogue-updated')); summary.focus();
        });
        grid.append(button);
      });
      more.hidden = visibleHeadboards(state.group).length <= limit;
    }
    state.syncHeadboards = () => {
      const selected = state.group.headboards.find(h => String(h.id) === state.headboardSelect.value);
      const key = state.select.value + ':' + state.headboardSelect.value;
      if (key === signature) return;
      signature = key;
      title.textContent = selected?.label || label('headboard', 'Headboard');
      thumb.hidden = !selected?.image || failedHeadboardImages.has(selected.image);
      if (selected?.image) thumb.src = selected.image;
      note.textContent = selected?.showroomNote || ''; note.hidden = !note.textContent;
      draw();
    };
    more.addEventListener('click', () => {
      const old = limit; limit += 12; draw();
      grid.children[old]?.focus();
    });
    details.append(grid, more, note);
    state.headboardPreview.replaceWith(details); state.headboardPreview = details;
  }
  function fabricPicker(state) {
    if (state.group.configurator) return;
    const fabrics = window.FultonsHarrison?.fabrics || [];
    if (!fabrics.length || state.group.kind !== 'bedset') return;
    const details = make('details', '', 'fh-fabrics');
    const summary = make('summary');
    const thumb = make('img'); thumb.hidden = true; thumb.alt = ''; thumb.width = 44; thumb.height = 44;
    const text = make('span', label('viewFabrics', 'View fabrics'));
    summary.append(thumb, text); details.append(summary);
    const filterLabel = make('label', label('fabricRange', 'Fabric range'), 'fultons-harrison-label');
    const filter = make('select', '', 'fh-fabrics__filter'); filter.id = 'HarrisonFabricRange-' + state.id; filterLabel.htmlFor = filter.id;
    filter.add(new Option(label('allFabrics', 'All fabrics'), ''));
    [...new Set(fabrics.map(f => f.range))].sort().forEach(range => filter.add(new Option(range, range)));
    const grid = make('div', '', 'fh-fabrics__grid');
    const more = make('button', label('moreFabrics', 'Show more'), 'fh-fabrics__more'); more.type = 'button';
    const note = make('p', label('fabricNote', 'Fabric availability and any supplement will be confirmed with your quote.'), 'fultons-harrison-note');
    let limit = 12;
    function draw() {
      const list = fabrics.filter(f => !filter.value || f.range === filter.value);
      grid.replaceChildren();
      list.slice(0, limit).forEach(fabric => {
        const button = make('button', '', 'fh-fabrics__sample'); button.type = 'button';
        button.setAttribute('aria-label', label('selectFabric', 'Select fabric') + ': ' + fabric.name);
        button.setAttribute('aria-pressed', String(state.fabric?.id === fabric.id));
        const image = make('img'); image.src = fabric.url; image.alt = ''; image.width = 160; image.height = 160; image.loading = 'lazy'; image.decoding = 'async';
        button.append(image, make('span', fabric.name));
        button.addEventListener('click', () => { state.fabric = fabric; thumb.src = fabric.url; thumb.hidden = false; text.textContent = label('fabric', 'Fabric') + ': ' + fabric.name; details.open = false; draw(); update(state); summary.focus(); });
        grid.append(button);
      });
      more.hidden = list.length <= limit;
    }
    filter.addEventListener('change', () => { limit = 12; draw(); });
    more.addEventListener('click', () => { limit += 12; draw(); });
    details.append(filterLabel, filter, grid, more, note);
    const caption = make('span', label('fabric', 'Fabric'), 'fultons-harrison-label');
    state.headboardPreview.after(caption, details);
    draw();
  }
  function bedConfigurator(state) {
    if (!state.group.configurator) return;
    const assets = window.FultonsHarrison.bedAssets;
    const ui=window.FultonsHarrisonBedLabels||{};
    state.baseId = 'shallow'; state.feet = {shallow:'natural',deep:'castors'};
    state.fabric = {id:'oxford-sand',name:'Oxford Sand'};
    const requestedVariant=new URL(location.href).searchParams.get('variant');
    const initial=Object.entries(state.group.variants||{}).find(([,id])=>String(id)===requestedVariant)?.[0];
    if(initial){const [width,head,base,foot]=initial.split('|');const sizeIndex=state.options.findIndex(o=>String(o.widthCm)===width);if(sizeIndex>=0)state.select.value=String(sizeIndex);state.headboardSelect.value=head;state.baseId=base;state.feet[base]=foot;}
    state.group.baseOptions = state.group.bases[state.baseId].options;
    const picker = (caption, choices, read, change) => {
      const details=make('details','','fh-fabrics fh-bed-picker');
      const summary=make('summary'),thumb=make('img'),name=make('span');thumb.alt='';thumb.width=44;thumb.height=44;summary.append(thumb,name);details.append(summary);
      const grid=make('div','','fh-fabrics__grid');details.append(grid);
      const draw=()=>{const list=choices(),selected=list.find(x=>x.id===read());thumb.src=selected.image;name.textContent=caption+': '+selected.label;grid.replaceChildren();for(const item of list){const b=make('button','','fh-fabrics__sample');b.type='button';b.setAttribute('aria-pressed',String(item.id===read()));b.setAttribute('aria-label',caption+': '+item.label);const im=make('img');im.src=item.image;im.alt='';im.width=96;im.height=96;b.append(im,make('span',item.label));b.addEventListener('click',()=>{change(item.id);details.open=false;draw();update(state);summary.focus();});grid.append(b);}};
      details.addEventListener('keydown',e=>{if(e.key==='Escape'){details.open=false;summary.focus();}});
      details.addEventListener('toggle',()=>{if(details.open)state.section.querySelectorAll('details.fh-fabrics').forEach(d=>{if(d!==details)d.open=false;});});draw();return {details,draw};
    };
    const footLabels={natural:'Natural beech',grey:'Grey',castors:'Standard castors',glides:'Glides'};
    const feetPicker=picker((ui.feet || 'Feet'),()=> (state.baseId==='deep'?['castors','glides']:['natural','grey']).map(id=>({id,label:footLabels[id],image:assets['original-foot-'+id+'.jpg']})),()=>state.feet[state.baseId],id=>{state.feet[state.baseId]=id;});
    const basePicker=picker((ui.base || 'Base'),()=>Object.entries(state.group.bases).map(([id,b])=>({id,label:b.label,image:assets['sample-base-'+id+'.png']})),()=>state.baseId,id=>{state.baseId=id;state.group.baseOptions=state.group.bases[id].options;feetPicker.draw();});
    state.headboardPreview.after(basePicker.details,feetPicker.details);
    const specification=make('p','Oxford Sand · Medium feel · Padded top · No drawers','fultons-harrison-note');
    const bedDetails=make('details','','fh-bed-details');
    const detailsSummary=make('summary',ui.details || 'Bed details & offers');
    const detailsContent=make('div','','fh-bed-details__content');
    detailsContent.append(specification,...state.section.querySelectorAll(':scope > .fultons-harrison-note:not(.fh-bed-includes)'),make('p',ui.image_note || 'Illustrative mattress preview. Fabric and mattress details may vary.','fultons-harrison-note'));
    bedDetails.append(detailsSummary,detailsContent);state.section.append(bedDetails);
    state.bedChoice=()=>state.group.bases[state.baseId].label+' · '+footLabels[state.feet[state.baseId]]+' · Oxford Sand · Medium';
    const gallery=state.root.querySelector('fultons-product-gallery');
    const visual=make('div','','fh-bed-visual');
    const open=make('button','','fh-bed-open');open.type='button';open.setAttribute('aria-label',(ui.enlarge || 'Enlarge configured bed'));
    const dialog=make('dialog','','fh-bed-dialog'),close=make('button',(ui.close || 'Close'),'fh-bed-close');close.type='button';dialog.append(close);document.body.append(dialog);close.addEventListener('click',()=>dialog.close());
    const status=make('p','','fultons-harrison-note');status.setAttribute('role','status');
    visual.append(open,status);
    if(gallery){gallery.before(visual);gallery.hidden=true;}else state.root.querySelector('.catalogue-proof__grid,.pdp-refined__grid')?.firstElementChild.prepend(visual);
    let signature='',request=0,art='';
    const affine=(from,to)=>{const [p,q,r]=from,[u,v,w]=to;const xx=q[0]-p[0],xy=q[1]-p[1],yx=r[0]-p[0],yy=r[1]-p[1],det=xx*yy-xy*yx;const a=((v[0]-u[0])*yy-(w[0]-u[0])*xy)/det,c=((w[0]-u[0])*xx-(v[0]-u[0])*yx)/det,b=((v[1]-u[1])*yy-(w[1]-u[1])*xy)/det,d=((w[1]-u[1])*xx-(v[1]-u[1])*yx)/det;return `matrix(${a} ${b} ${c} ${d} ${u[0]-a*p[0]-c*p[1]} ${u[1]-b*p[0]-d*p[1]})`;};
    const anchor=[[607,1370],[1325,1467],[1898,1289]];
    const overlayTransform=affine([[350,793],[768,849],[1097,745]],anchor);
    const load=src=>new Promise((resolve,reject)=>{const im=new Image();im.onload=resolve;im.onerror=reject;im.src=src;});
    state.syncBedImage=async()=>{const h=state.headboardSelect.value,f=state.feet[state.baseId],key=h+'-'+state.baseId+'-'+f;if(signature===key)return;signature=key;const token=++request;const baseSrc=assets[state.baseId==='deep'?`original-deep-${h}-${f}.png`:`original-${h}-${f}.png`],mattressSrc=assets['fitted-'+state.group.mattressId+'.png'];status.textContent=(ui.updating || 'Updating your bed…');open.disabled=true;try{await Promise.all([load(baseSrc),load(mattressSrc)]);if(token!==request)return;const transform=state.baseId==='deep'?affine(anchor,[[610,1327],[1325,1420],[1898,1248]]):'matrix(1 0 0 1 0 0)';const clipId='fh-clip-'+state.id;art=`<svg viewBox="495 650 1540 1100" role="img" aria-label="Your configured bed in Oxford Sand"><image width="2500" height="1875" href="${baseSrc}"/><defs><clipPath id="${clipId}"><path d="M609 1225 Q920 1139 1280 1108 L1850 1140 Q1895 1142 1900 1170 L1898 1289 L1362 1461 Q1325 1477 1280 1468 L607 1370 Q601 1320 609 1225Z"/></clipPath></defs><g transform="${transform}"><g clip-path="url(#${clipId})"><image width="1448" height="1086" href="${mattressSrc}" transform="${overlayTransform}"/></g></g></svg>`;open.innerHTML=art;open.append(make('span',(ui.enlarge_image || 'Enlarge image'),'fh-bed-enlarge'));open.disabled=false;status.textContent='';}catch{if(token===request){status.textContent=(ui.image_error || 'Unable to load this preview. Please refresh the page.');open.replaceChildren();signature='';}}};
    open.addEventListener('click',()=>{dialog.querySelector('svg')?.remove();dialog.insertAdjacentHTML('beforeend',art.replaceAll('fh-clip-','fh-zoom-clip-'));dialog.showModal();});
    state.bedDialog=dialog;
    const form=make('form','','fh-bed-purchase');form.method='post';form.action='/cart/add';const idInput=make('input');idInput.type='hidden';idInput.name='id';
    for(const [name,value] of [['properties[Fabric]','Oxford Sand'],['properties[Feel]','Medium'],['quantity','1']]){const input=make('input');input.type='hidden';input.name=name;input.value=value;form.append(input);}
    const buy=make('button',(ui.buy || 'Add to cart'),'fh-bed-buy');buy.type='submit';buy.disabled=true;const result=make('p','','fultons-harrison-note');result.setAttribute('role','status');form.append(idInput,buy,result);state.section.append(form);
    state.syncPurchase=()=>{const width=state.options[Number(state.select.value)].widthCm;const key=width+'|'+state.headboardSelect.value+'|'+state.baseId+'|'+state.feet[state.baseId];const id=state.group.variants?.[key];idInput.value=id||'';buy.disabled=!id;if(id){const url=new URL(location.href);url.searchParams.set('variant',id);history.replaceState(history.state,'',url.href);}};
    form.addEventListener('submit',async e=>{e.preventDefault();if(!idInput.value||buy.disabled)return;buy.disabled=true;buy.textContent=(ui.adding || 'Adding…');result.textContent='';try{const response=await fetch('/cart/add.js',{method:'POST',body:new FormData(form),headers:{Accept:'application/json'}});const body=await response.json();if(!response.ok)throw new Error(body.description||(ui.cart_error || 'Unable to add this bed. Please try again.'));location.assign('/cart');}catch(error){result.textContent=error.message;buy.disabled=false;buy.textContent=(ui.buy || 'Add to cart');}});
  }

  function render() {
    scheduled = false;
    const products = window.FultonsHarrison?.products || {};
    armPromotionTimer(products);
    updateMenuOffers(products);
    const menuChanged = updateMenu(products);
    if (menuChanged) document.dispatchEvent(new CustomEvent('fultons:catalogue-updated'));
    const id = String(new URL(location.href).searchParams.get('draft_product') || window.FultonsPriceTrial?.productId || '');
    const sourceGroup = products[id];
    const group = sourceGroup?.kind === 'bedset' ? { ...sourceGroup, headboards: visibleHeadboards(sourceGroup) } : sourceGroup;
    const root = document.querySelector('#MainContent .catalogue-proof:not([hidden]),#MainContent .pdp-refined:not([hidden])');
    if (current && (current.id !== id || current.root !== root || !current.section.isConnected)) cleanup();
    if (!root || !group) return;
    if (current) { update(current); return; }
    const options = (group.options || []).filter(option => option.label && Number.isFinite(option.priceGbp) && option.priceGbp > 0 && (group.kind !== 'bedset' || group.headboards?.some(headboard => calculate(group, option, headboard))));
    const title = root.querySelector('h1');
    const info = root.querySelector('.fultons-product-info') || title?.parentElement;
    const description = info?.querySelector('.catalogue-proof__description,.pdp-refined__intro');
    if (!options.length || !info || !description) return;
    let headline = info.querySelector(':scope > .fultons-retail-price');
    const createdHeadline = !headline;
    if (!headline) { headline = make('p', '', 'fultons-retail-price'); description.after(headline); }
    const previousPrice = [...headline.childNodes];
    // Existing price-owner guard prevents the generic furniture renderer rounding these prices.
    headline.dataset.fixedPiecePrice = 'true';
    const hiddenPrices = [...info.querySelectorAll(':scope > .catalogue-proof__price,:scope > .pdp-refined__price')].map(node => [node, node.hidden]);
    hiddenPrices.forEach(([node]) => { node.hidden = true; });
    const section = make('section', '', 'fultons-product-options fultons-harrison-options');
    section.dataset.harrisonProduct = id;
    if(group.configurator)section.dataset.bedConfigurator='true';
    section.append(make('h2', label('designHeading', 'Choose your design')));
    const caption = make('label', label('size', 'Size'), 'fultons-harrison-label');
    const select = make('select', '', 'fultons-harrison-select');
    select.id = 'FultonsHarrisonSize-' + id;
    caption.htmlFor = select.id;
    options.forEach((option, index) => select.add(new Option(option.label, String(index))));
    section.append(caption, select);
    let headboardSelect = null, headboardPreview = null, offerNote = null, upcomingNote = null;
    if (group.kind === 'bedset') {
      const headboardLabel = make('span', label('headboard', 'Headboard'), 'fultons-harrison-label');
      headboardSelect = make('select', '', 'fultons-harrison-select');
      headboardSelect.id = 'FultonsHarrisonHeadboard-' + id;

      group.headboards.forEach(headboard => headboardSelect.add(new Option(headboard.label, String(headboard.id))));
      headboardPreview = make('div', '', 'fultons-harrison-headboard-preview');
      section.append(headboardLabel, headboardSelect, headboardPreview);
      if(group.headboardCompatibilityNote) section.append(make('p',group.headboardCompatibilityNote,'fultons-harrison-note'));
      section.append(make('p', group.fixedBaseNote || label('shallowBase', 'Shallow divan base'), 'fultons-harrison-note'));
    }
    if (promotionsFor(group).length) {
      offerNote = make('p', '', 'fultons-harrison-note fultons-harrison-offer');
      offerNote.setAttribute('aria-live', 'polite'); section.append(offerNote);
      upcomingNote = make('p', '', 'fultons-harrison-note'); upcomingNote.hidden = true; section.append(upcomingNote);
    }
    if (group.unitNote) section.append(make('p', group.unitNote, 'fultons-harrison-note' + (group.configurator ? ' fh-bed-includes' : '')));
    if (group.showroomNote) section.append(make('p', group.showroomNote, 'fultons-harrison-note'));
    if (!group.content && group.details?.length) {
      const details = make('details', '', 'fultons-harrison-details');
      details.append(make('summary', label('details', 'Details')));
      const list = make('ul');
      group.details.filter(Boolean).forEach(detail => list.append(make('li', detail)));
      details.append(list); section.append(details);
    }
    const offerTag = make('span', '', 'fultons-harrison-product-offer');
    offerTag.hidden = true;
    offerTag.dataset.harrisonProductOffer = id;
    offerTag.setAttribute('aria-live', 'polite');
    headline.after(section);
    current = { id, group, options, root, section, select, headboardSelect, headboardPreview, offerNote, offerTag, upcomingNote, headline, createdHeadline, previousPrice, hiddenPrices };
    const state = current;
    headboardPicker(state);
    fabricPicker(state);
    bedConfigurator(state);
    const change = () => { update(state); document.dispatchEvent(new CustomEvent('fultons:catalogue-updated')); };
    select.addEventListener('change', change);
    headboardSelect?.addEventListener('change', change);
    update(state);
    // Let the common layout owner position the compact disclosure and price.
    document.dispatchEvent(new CustomEvent('fultons:catalogue-updated'));
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(render);
  }
  document.addEventListener('fultons:catalogue-updated', schedule);
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('popstate', schedule);
  window.addEventListener('pagehide', clearPromotionTimer);
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})();

